import { useEffect, useRef, useState } from 'react';

interface RevealOptions {
  /** 0–1 portion of the element that must be visible to trigger. */
  threshold?: number;
  /** Trigger margin, e.g. "0px 0px -10% 0px" to fire slightly early. */
  rootMargin?: string;
  /** Reveal only once (default) or every time it re-enters. */
  once?: boolean;
}

/**
 * IntersectionObserver-based scroll reveal. Returns a ref to attach to the
 * target and a boolean indicating whether it has entered the viewport.
 *
 * If IntersectionObserver is unavailable (or motion is reduced upstream),
 * callers can simply treat `isVisible` as true on mount via the fallback.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -8% 0px',
  once = true,
}: RevealOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // No IO support → reveal immediately so content is never hidden.
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}
